import React, { useState } from 'react'
import useBreakpoint from '../../hooks/useBreakpoint.jsx'

const theme = {
  colors: {
    greenOnions: '#3BB273',
    greenLight: '#E8F5EE',
    grey200: '#E7E5E4',
    grey500: '#78716C',
    white: '#FFFFFF',
    charcoal: '#222725',
    dropAmber: '#B45309',
    liftTeal: '#0E7490',
  },
  radius: { md: '10px', sm: '6px' },
}

// Column-count policy for the tier-picker grids.
// Phone (≤699px) collapses to 2 columns so 5–7 tiers never overflow.
// Off-phone we honour an explicit `columns` prop, else one column per option.
export function gridColumnsForBreakpoint({ breakpoint, optionCount, explicit }) {
  if (breakpoint === 'phone') return 2
  return explicit ?? optionCount
}

export const REPAIR_CONDITION_OPTIONS = [
  { value: 'Mint',      label: 'Like New',   hint: 'Essentially new. No signs of use.' },
  { value: 'Excellent', label: 'Excellent',  hint: 'Minimal signs of use. Near-perfect cosmetically.' },
  { value: 'Very Good', label: 'Very Good',  hint: 'Some signs of use but well-maintained. Fully functional.' },
  { value: 'Good',      label: 'Good',       hint: 'Visible surface wear. Everything works properly.' },
  { value: 'Fair',      label: 'Fair',       hint: 'Noticeable cosmetic flaws. Fully functional after repair.' },
]

export const TESTING_CONDITION_OPTIONS = [
  {
    value: 'Broken',
    label: 'Spares or Repair',
    hint: 'simply does not work. Sold for parts or repair only.',
    guide: {
      indicators: [
        "Won't power on, or has a fundamental failure (broken neck, dead mixing board)",
        'Missing the components it needs to function',
        'All faults must be documented in the listing',
      ],
      liftTo: { tier: 'Fair', reason: 'it actually works once you get past the imperfections' },
    },
  },
  {
    value: 'Fair',
    label: 'Fair',
    hint: 'imperfections that impact functionality — not broken, but compromised.',
    guide: {
      indicators: [
        'Scratchy pots, sliders, or switches',
        'Dead spots in the upper register, fret buzz, or other minor playing issues',
        'Top cracks or similar that affect tone or projection',
      ],
      dropTo: { tier: 'Spares or Repair', reason: "it doesn't power on, or the functional problems need repair before sale" },
      liftTo: { tier: 'Good', reason: 'the issue is purely cosmetic' },
    },
  },
  {
    value: 'Good',
    label: 'Good',
    hint: 'significant cosmetic imperfections, but in fine working order.',
    guide: {
      indicators: [
        'Scratches and dents on chassis or finish from normal use',
        'Visible cracks in binding or corrosion on hardware',
        'Still fully playable; every control behaves as expected',
      ],
      dropTo: { tier: 'Fair', reason: 'anything has started to affect playability (scratchy pots, dead spots)' },
      liftTo: { tier: 'Very Good', reason: "there's no significant chassis damage — only light dings" },
    },
  },
  {
    value: 'Very Good',
    label: 'Very Good',
    hint: 'more visible cosmetic defects than Excellent, but still in great shape.',
    guide: {
      indicators: [
        'Light scratches, finish dings, or buckle rash from normal handling',
        'No significant chassis damage',
        'Nothing that affects playability or function',
      ],
      dropTo: { tier: 'Good', reason: 'marks are significant (large dents, deep scratches, stains)' },
      liftTo: { tier: 'Excellent', reason: 'marks are only visible on close inspection' },
    },
  },
  {
    value: 'Excellent',
    label: 'Excellent',
    hint: 'used, but only minor cosmetic defects.',
    guide: {
      indicators: [
        'Light surface marks only — e.g. pick marks on a guitar, stick marks on a drum head',
        'All controls behave as new; fully functional',
        'Comes complete with accessories where applicable',
      ],
      dropTo: { tier: 'Very Good', reason: 'you see more than minor marks (scratches, finish dings, buckle rash)' },
      liftTo: { tier: 'Like New', reason: 'it is genuinely blemish-free' },
    },
  },
  {
    value: 'Mint',
    label: 'Like New',
    hint: 'completely blemish-free, as if fresh from the factory.',
    guide: {
      indicators: [
        'No scratches, dings, or wear anywhere — even on close inspection',
        'Original packaging and accessories present where applicable',
        'Has not been modified in any way',
      ],
      dropTo: { tier: 'Excellent', reason: 'you see any signs of use, however minor' },
    },
  },
]

export default function ConditionPicker({ options, value, onChange, columns }) {
  const [hoveredValue, setHoveredValue] = useState(null)
  const breakpoint = useBreakpoint()
  const cols = gridColumnsForBreakpoint({ breakpoint, optionCount: options.length, explicit: columns })

  const activeOpt = options.find(o => o.value === value)
  const hoveredOpt = hoveredValue ? options.find(o => o.value === hoveredValue) : null

  // Hovering a non-active tier shows that tier's headline only (preview).
  // Otherwise we show the active tier — expanded if it has a guide, simple headline if not.
  const previewing = hoveredOpt && hoveredOpt.value !== activeOpt?.value
  const shownOpt = previewing ? hoveredOpt : activeOpt
  const isExpanded = !previewing && !!activeOpt?.guide

  const hasGuide = options.some(o => o.guide)

  return (
    <div>
      <div
        onMouseLeave={() => setHoveredValue(null)}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: '8px',
          marginBottom: shownOpt ? '10px' : '0',
        }}
      >
        {options.map(opt => {
          const selected = value === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              onMouseEnter={() => setHoveredValue(opt.value)}
              style={{
                padding: '10px 8px',
                border: `2px solid ${selected ? theme.colors.greenOnions : theme.colors.grey200}`,
                borderRadius: theme.radius.md,
                background: selected ? theme.colors.greenLight : theme.colors.white,
                fontWeight: selected ? 600 : 400,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                color: theme.colors.charcoal,
              }}
            >
              {opt.label}
            </button>
          )
        })}
      </div>

      {shownOpt && !isExpanded && (
        <div style={{
          fontSize: '12px',
          color: theme.colors.grey500,
          padding: '6px 10px',
          background: '#F8F9F8',
          borderRadius: theme.radius.sm,
          borderLeft: `3px solid ${theme.colors.greenOnions}`,
          minHeight: '28px',
          maxWidth: '640px',
        }}>
          <strong style={{ color: theme.colors.charcoal, fontWeight: 600 }}>{shownOpt.label}</strong>
          {' — '}{shownOpt.hint}
        </div>
      )}

      {shownOpt && isExpanded && (
        <div style={{
          fontSize: '12.5px',
          color: theme.colors.charcoal,
          padding: '12px 14px',
          background: '#F8F9F8',
          borderRadius: theme.radius.sm,
          borderLeft: `3px solid ${theme.colors.greenOnions}`,
          maxWidth: '640px',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.4 }}>
            {shownOpt.label} — {shownOpt.hint}
          </div>
          <div style={{ borderTop: `1px solid ${theme.colors.grey200}`, margin: '10px 0' }} />
          <div style={{
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.5px',
            color: theme.colors.grey500,
            textTransform: 'uppercase',
            marginBottom: '6px',
          }}>
            How to tell it's {shownOpt.label}
          </div>
          <ul style={{ margin: 0, paddingLeft: '18px', lineHeight: 1.5 }}>
            {shownOpt.guide.indicators.map((ind, i) => (
              <li key={i} style={{ marginBottom: '2px' }}>{ind}</li>
            ))}
          </ul>
          {(shownOpt.guide.dropTo || shownOpt.guide.liftTo) && (
            <div style={{ marginTop: '10px', lineHeight: 1.5 }}>
              {shownOpt.guide.dropTo && (
                <div>
                  <strong style={{ color: theme.colors.dropAmber, fontWeight: 600 }}>
                    ↓ Drop to {shownOpt.guide.dropTo.tier}
                  </strong>
                  <span style={{ color: theme.colors.grey500 }}>
                    {' '}if {shownOpt.guide.dropTo.reason}.
                  </span>
                </div>
              )}
              {shownOpt.guide.liftTo && (
                <div>
                  <strong style={{ color: theme.colors.liftTeal, fontWeight: 600 }}>
                    ↑ Lift to {shownOpt.guide.liftTo.tier}
                  </strong>
                  <span style={{ color: theme.colors.grey500 }}>
                    {' '}if {shownOpt.guide.liftTo.reason}.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {hasGuide && (
        <div
          title="Adapted from Reverb's gear condition guide."
          style={{
            fontSize: '11px',
            color: theme.colors.grey500,
            fontStyle: 'italic',
            marginTop: '8px',
            textAlign: 'center',
            cursor: 'help',
          }}
        >
          If you're between two tiers, pick the lower one. <span style={{ fontStyle: 'normal' }}>ⓘ</span>
        </div>
      )}
    </div>
  )
}
